import { Box, Skeleton, Stack, Typography } from "@mui/material";
import React from "react";
import { SparklingGradientMultiStar } from "SvgIcons/SparklingGradientStar";
import { KBO_BANNER_GRADIENT } from "./kboSubscriptionStyles";

type Props = {
    id: string;
    title: string;
    loading: boolean;
    /** The summary line under the title; wrap counts in {@link BannerHighlight}. */
    summary: React.ReactNode;
    action?: React.ReactNode;
};

/** Gradient header shared by the KBo Settings subscription pages. */
export function SubscriptionsBanner({
    id,
    title,
    loading,
    summary,
    action,
}: Props) {
    return (
        <Stack
            id={id}
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            rowGap={1.5}
            columnGap={2}
            sx={{
                borderRadius: "12px",
                padding: "18px 22px",
                background: KBO_BANNER_GRADIENT,
                boxShadow: "0 6px 18px rgba(29, 36, 96, 0.25)",
                color: "#FFFFFF",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                columnGap={2}
                flex={1}
                minWidth={0}
            >
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: "10px",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    <SparklingGradientMultiStar />
                </Box>
                <Stack minWidth={0}>
                    <Typography
                        component="h1"
                        color="inherit"
                        sx={{
                            fontSize: 18,
                            lineHeight: "26px",
                            fontWeight: 600,
                        }}
                    >
                        {title}
                    </Typography>
                    {loading ? (
                        <Skeleton
                            width={360}
                            sx={{ bgcolor: "rgba(255, 255, 255, 0.2)" }}
                        />
                    ) : (
                        <Typography
                            color="inherit"
                            id={`${id}-summary`}
                            sx={{ fontSize: 14, lineHeight: "20px" }}
                        >
                            {summary}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            {action && (
                <Box flexShrink={0} color="inherit">
                    {action}
                </Box>
            )}
        </Stack>
    );
}

/** The teal emphasis used for counts inside a banner summary. */
export function BannerHighlight({ children }: { children: React.ReactNode }) {
    return (
        <Box component="span" sx={{ fontWeight: 700, color: "#7FE9D7" }}>
            {children}
        </Box>
    );
}
